<template>
  <div id="app">
    <router-view />
    <Watermark />
  </div>
</template>

<script>
import Watermark from './components/Watermark.vue';

export default {
  name: 'App',
  components: {
    Watermark
  }
};
</script>

<style>
:root {
  --app-bg: linear-gradient(180deg, #f4f9ff 0%, #e9f2ff 100%);
  --surface-primary: #ffffff;
  --surface-secondary: #f3f8ff;
  --surface-strong: #0f4fb9;
  --surface-strong-alt: #163b8c;
  --text-primary: #10203d;
  --text-secondary: #5e6c87;
  --text-muted: #8492a8;
  --border-soft: #d7e5fb;
  --border-strong: #bfd5fb;
  --brand-500: #1677ff;
  --brand-600: #0f5fd5;
  --brand-700: #1548a5;
  --success-500: #16a56f;
  --warning-500: #d88312;
  --danger-500: #dc5c47;
  --shadow-soft: 0 18px 48px rgba(14, 68, 150, 0.12);
  --shadow-card: 0 10px 28px rgba(14, 68, 150, 0.1);
  --shell-width: 1240px;
  color-scheme: light;
}

/* 全局样式重置 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.van-toast {
  min-width: 300px !important;
  max-width: 300px !important;
  box-sizing: border-box;
}

/* 统一自定义确认弹窗样式（全局） */
.custom-confirm-dialog {
  padding: 32px 24px 24px;
  text-align: center;
  background: white;
  border-radius: 16px;
}

.confirm-icon {
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
}

.confirm-title {
  font-size: 18px;
  font-weight: bold;
  color: #323233;
  margin-bottom: 12px;
}

.confirm-message {
  font-size: 14px;
  color: #646566;
  line-height: 1.5;
  margin-bottom: 24px;
}

.confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.confirm-actions .van-button {
  min-width: 80px;
  height: 36px;
  border-radius: 18px;
  font-size: 15px;
}

html, body {
  height: 100%;
  font-family: 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: var(--app-bg);
  color: var(--text-primary);
}

#app {
  height: 100%;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(22, 119, 255, 0.14), transparent 26%),
    radial-gradient(circle at right 10% top 14%, rgba(35, 123, 255, 0.12), transparent 24%),
    var(--app-bg);
}

/* 自定义滚动条样式 */
::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* 全局动画 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.slide-enter-active, .slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter, .slide-leave-to {
  transform: translateX(100%);
}

/* 响应式设计 */
@media (max-width: 320px) {
  html {
    font-size: 14px;
  }
}

@media (min-width: 321px) and (max-width: 375px) {
  html {
    font-size: 15px;
  }
}

@media (min-width: 376px) and (max-width: 414px) {
  html {
    font-size: 16px;
  }
}

@media (min-width: 415px) {
  html {
    font-size: 17px;
  }
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  html, body {
    background-color: #1a1a1a;
    color: #ffffff;
  }
  
  #app {
    background-color: #1a1a1a;
  }
}

/* 安全区域适配 */
@supports (padding: max(0px)) {
  .safe-area-inset-top {
    padding-top: max(20px, env(safe-area-inset-top));
  }
  
  .safe-area-inset-bottom {
    padding-bottom: max(20px, env(safe-area-inset-bottom));
  }
  
  .safe-area-inset-left {
    padding-left: max(16px, env(safe-area-inset-left));
  }
  
  .safe-area-inset-right {
    padding-right: max(16px, env(safe-area-inset-right));
  }
}

/* 触摸优化 */
.touchable {
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

/* 防止页面缩放 */
.no-zoom {
  touch-action: manipulation;
}

/* 自定义工具类 */
.text-center {
  text-align: center;
}

.text-left {
  text-align: left;
}

.text-right {
  text-align: right;
}

.flex {
  display: flex;
}

.flex-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

.flex-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.flex-column {
  display: flex;
  flex-direction: column;
}

.mt-8 {
  margin-top: 8px;
}

.mt-16 {
  margin-top: 16px;
}

.mb-8 {
  margin-bottom: 8px;
}

.mb-16 {
  margin-bottom: 16px;
}

.p-8 {
  padding: 8px;
}

.p-16 {
  padding: 16px;
}

.rounded {
  border-radius: 8px;
}

.rounded-lg {
  border-radius: 12px;
}

.shadow {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.shadow-lg {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

/* 覆盖Vant默认样式 */
.van-nav-bar {
  background: linear-gradient(135deg, var(--surface-strong) 0%, var(--surface-strong-alt) 100%) !important;
  box-shadow: 0 14px 36px rgba(11, 55, 127, 0.18);
}

.van-nav-bar .van-nav-bar__title {
  color: white !important;
  font-weight: 700 !important;
}

.van-nav-bar .van-nav-bar__left,
.van-nav-bar .van-nav-bar__right {
  color: white !important;
}

.van-tabbar-item--active {
  color: var(--brand-500) !important;
}

.van-button--primary {
  background: linear-gradient(135deg, var(--brand-500) 0%, var(--brand-700) 100%) !important;
  border: none !important;
  box-shadow: 0 14px 30px rgba(22, 119, 255, 0.24);
}

.van-button--primary:active {
  background: linear-gradient(135deg, var(--brand-600) 0%, var(--brand-700) 100%) !important;
}

/* 卡片样式增强 */
.van-card {
  box-shadow: var(--shadow-card) !important;
  border-radius: 18px !important;
  border: 1px solid var(--border-soft) !important;
  overflow: hidden !important;
  margin-bottom: 6px !important;
}

.van-card:last-child {
  margin-bottom: 0 !important;
}

/* 输入框样式增强 */
.van-field__control {
  font-size: 16px !important;
}

.van-field--error .van-field__control {
  color: #ee0a24 !important;
}

/* 弹窗样式增强 */
.van-popup {
  border-radius: 16px 16px 0 0 !important;
}

.van-popup--center {
  border-radius: 16px !important;
}

/* 标签样式增强 */
.van-tag {
  border-radius: 12px !important;
  font-weight: 500 !important;
}

/* 加载状态样式 */
.van-loading {
  color: var(--brand-500) !important;
}

/* 空状态样式 */
.van-empty {
  padding: 40px 16px !important;
}

.van-empty__description {
  color: #969799 !important;
  font-size: 14px !important;
}

/* 图片预览样式 */
.van-image-preview {
  background: rgba(0, 0, 0, 0.9) !important;
}

.van-image-preview__close-icon {
  color: #fff !important;
  background: rgba(0, 0, 0, 0.42);
  border-radius: 999px;
}

.page-pull-refresh {
  min-height: 100%;
}

.page-pull-refresh > .van-pull-refresh__track {
  min-height: 100%;
}

@media (min-width: 768px) {
  .van-image-preview__swipe-item {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .van-image-preview__image {
    width: auto !important;
    max-width: 100% !important;
    max-height: 100% !important;
    margin: 0 auto !important;
  }

  .van-image-preview__image .van-image,
  .van-image-preview__image img {
    display: block !important;
    max-width: 100% !important;
    max-height: 100% !important;
    margin: 0 auto !important;
  }

  .van-image-preview__close-icon {
    top: 24px !important;
    right: 24px !important;
    padding: 8px;
    font-size: 28px !important;
    z-index: 2100;
  }
}

/* 选择器样式 */
.van-picker {
  background: white !important;
}

.van-picker__toolbar {
  background: #f4f8ff !important;
  border-bottom: 1px solid var(--border-soft) !important;
}

.van-cell,
.van-field,
.van-dropdown-menu__bar,
.van-dialog,
.van-popup--center {
  background: var(--surface-primary) !important;
}

.van-field,
.van-cell {
  color: var(--text-primary) !important;
}

.van-field__label,
.van-cell__title,
.van-cell__label {
  color: var(--text-secondary) !important;
}

.van-dropdown-menu__bar {
  box-shadow: none !important;
}

.van-button--default {
  color: var(--brand-700) !important;
  border-color: var(--border-strong) !important;
  background: rgba(255, 255, 255, 0.9) !important;
}

/* 深色模式下的Vant组件样式 */
@media (prefers-color-scheme: dark) {
  .van-card {
    background: #2a2a2a !important;
    color: white !important;
  }
  
  .van-field {
    background: #2a2a2a !important;
  }
  
  .van-field__label {
    color: #ccc !important;
  }
  
  .van-field__control {
    color: white !important;
    background: transparent !important;
  }
  
  .van-cell {
    background: #2a2a2a !important;
    color: white !important;
  }
  
  .van-popup {
    background: #2a2a2a !important;
  }
  
  .van-picker {
    background: #2a2a2a !important;
  }
  
  .van-picker__toolbar {
    background: #333 !important;
    border-bottom-color: #444 !important;
  }
  
  .van-picker-column__item {
    color: white !important;
  }
  
  .van-dialog {
    background: #2a2a2a !important;
  }
  
  .van-dialog__header {
    color: white !important;
  }
  
  .van-dialog__message {
    color: #ccc !important;
  }
}
</style>
